<template>
  <el-dialog
    class="meeting-detail"
    append-to-body
    :model-value="visible"
    :show-close="false"
    destroy-on-close
    @close="$emit('close')"
  >
    <template #header>
      <div class="flex justify-end ">
        <img
          data-testid="my-calendar.meeting-card.close-btn"
          src="@/assets/my-calendar/close.svg"
          class="w-6 h-6 cursor-pointer"
          @click="emit('close')" />
      </div>
    </template>

    <Loading :loading="isProcessing" loading-text="Loading..." background="none-background">
      <div data-testid="my-calendar.meeting-card.content" class="meeting-card-content">
        <div class="flex items-center gap-3 mb-5">

          <span data-testid="my-calendar.meeting-card.title-text">{{ meetingTitle }}</span>
          <div v-if="!meeting.is_conflicted" data-testid="my-calendar.meeting-card.available-status-chip" class="status-chip available"> Available </div>
          <div v-if="meeting.is_conflicted" data-testid="my-calendar.meeting-card.conflict-status-chip" class="status-chip conflict"> Conflict </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="flex flex-col gap-3  text-sm text-[#666666] w-[140px]">
            <div>Patient Name:</div>
            <div>Time:</div>
            <div>Appointment Type:</div>
            <div>Location:</div>
          </div>
          <div class="flex flex-col gap-3 text-sm">
            <div data-testid="my-calendar.meeting-card.patient-name-text">{{ meeting.patient_firstname + ' ' + meeting.patient_lastname }}</div>
            <div data-testid="my-calendar.meeting-card.time-text">{{ formatedDate(meeting.requested_start_time,meeting.requested_end_time) }}</div>
            <div data-testid="my-calendar.meeting-card.appointment-type-text">{{ meeting.event_type || 'N/A' }}</div>
            <div data-testid="my-calendar.meeting-card.location-text">{{ meeting.location || 'N/A'   }}</div>
          </div>
        </div>
      </div>
    </Loading>


    <template #footer>
      <div v-if="isProcessing" class="action-bar footer-placeholder"></div>
      <div v-else-if="action" data-testid="my-calendar.meeting-card.action-result-text" class="action-bar">
        <div v-if="action === 'decline'" class="action-btn w-full">
          <img class="inline-block " src="@/assets/my-calendar/meeting-request/red-cross.svg" alt="decline" />
          <span style="color: #D10000;">Appointment Declined</span>
        </div>
        <div v-if="action === 'accept'" class="action-btn w-full">
          <img class="inline-block " src="@/assets/my-calendar/meeting-request/green-checked.svg" alt="confirm" />
          <span style="color: #0A6629;">Appointment Accepted</span>
        </div>

      </div>
      <div v-else class="action-bar">
        <div data-testid="my-calendar.meeting-card.decline-btn" class="action-btn decline" @click="handleAction('decline')">
          <img class="inline-block" src="@/assets/my-calendar/meeting-request/red-cross.svg" alt="decline" />
          Decline
        </div>
        <div data-testid="my-calendar.meeting-card.reschedule-btn" class="action-btn reschedule" @click="handleAction('reschedule')">
          <img class="inline-block" src="@/assets/my-calendar/meeting-request/yellow-question.svg" alt="reschedule" />
          Reschedule
        </div>
        <div data-testid="my-calendar.meeting-card.accept-btn" class="action-btn accept" @click="handleAction('accept')">
          <img class="inline-block" src="@/assets/my-calendar/meeting-request/green-checked.svg" alt="confirm" />
          Accept
        </div>
      </div>
    </template>

  </el-dialog>

</template>

<script setup>
import { computed } from 'vue'
import Loading from '@/NewLibrary/Loading/Loading.vue'
import moment from 'moment-timezone'
import { useStore } from 'vuex'
  
const store = useStore()
const emit = defineEmits(['close','remove','action-start','action-success','open-schedule-dialog'])

const { meeting, visible, actionState } = defineProps({
  meeting: Object,
  visible: Boolean,
  actionState: {
    type: Object,
    default: null
  }
})

const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const isProcessing = computed(() => actionState?.phase === 'loading')
const action = computed(() => actionState?.phase === 'success' ? actionState.action : '')
  
const meetingTitle = computed(() => {
  return `${meeting.topic} - ${moment.utc(meeting.requested_start_time).tz(calendarTimezone.value).format('dddd, MMMM D')}`
})
  
const formatedDate = (startStr,endStr) => {
  const datePart = moment.utc(startStr).tz(calendarTimezone.value).format('dddd, MMMM D')
  const startTime = moment.utc(startStr).tz(calendarTimezone.value).format('h:mma')
  const endTime = moment.utc(endStr).tz(calendarTimezone.value).format('h:mma')

  return `${datePart}, ${startTime}-${endTime}`;
};

const handleAction = async (val) => {
  // console.log('handleAction',val)
  // return
  if (isProcessing.value) {
    return
  }

  const firstNameInitial = meeting.patient_firstname ? `${meeting.patient_firstname.charAt(0)}.` : '';
  let meetingRequestTitle = meeting.patient_lastname ? `${firstNameInitial} ${meeting.patient_lastname}`.trim() : '';
  // dispatch update meeting request api
  const eventInput = {
    event_title: meetingRequestTitle,
    start_time: meeting.requested_start_time,
    end_time: meeting.requested_end_time,
    creator_calendar_id: store.state.calendar.creatorCalendarId,
    practice_id: meeting.clinic.clinic_id,
    participants:[
      {
        participant_calendar_id: meeting.patient_calendar_id,
        role: 'patient'
      },
      {
        participant_calendar_id: meeting.provider_calendar_id,
        role: 'provider'
      }
    ]
  }
  if(val === 'reschedule') {
    emit('open-schedule-dialog', meeting)
    emit('close')
    return
  }

  emit('action-start', val)

  if(val === 'accept') {
    await store.dispatch('calendar/updateMeetingRequest', { 
      id: meeting.uuid, 
      status: 'accepted',
      event_input: eventInput
    })
    await store.dispatch('calendar/fetchMeetingRequests');
    const userInfo = store.state.login.userInfo;
    const isClinic150105 = userInfo?.clinic_id === 150105;
    const selectedClinicianCalendarIds = store.getters['calendar/selectedClinicianCalendarIds'] || []
    const calendarIds = isClinic150105 
      ? selectedClinicianCalendarIds
      : [store.state.calendar.creatorCalendarId];

    if (isClinic150105 && calendarIds.length === 0) {
      store.commit('calendar/SET_RAW_EVENT_LIST', [])
      emit('action-end', val)
      return
    }
    
    store.commit('calendar/SET_CALENDAR_LOADING', true);
    await store.dispatch('calendar/fetchEvents', {
      calendar_ids: calendarIds,
      start_time: moment.utc('1970-01-01').startOf('day').toISOString(),
      end_time: moment().add(2, 'years').endOf('day').toISOString()
    })
    store.commit('calendar/SET_CALENDAR_LOADING', false);

  }else if(val === 'decline') {
    await store.dispatch('calendar/updateMeetingRequest', { 
      id: meeting.uuid, 
      status: 'rejected' ,
      cancellation_reason: 'Provider Declined - Test'
    })
    await store.dispatch('calendar/fetchMeetingRequests');
  }

  emit('action-success', val)

  emit('close')
  setTimeout(() => {
    emit('remove')
  }, 500)
  
}
</script>
<style scoped>
.action-bar {
  display: flex;
  justify-content: space-between;
  
  background-color: #F9FBFD;
  color: #666666;
  padding: 0px 6px;
  border-radius: 4px;
}
.footer-placeholder {
  min-height: 48px;
  background-color: transparent;
}
.meeting-card-content {
  min-height: 120px;
}
.action-btn {
  display:flex ;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  padding: 12px 24px;
  gap:6px;
}
.action-btn:hover {
  text-decoration: underline;
}
.action-btn.decline:hover {
  color: #D10000;
}
.action-btn.reschedule:hover {
  color: #E0961E;
}
.action-btn.accept:hover {
  color: #0A6629;
}
.status-chip{
  padding: 3px 12px;
  border-radius: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}
.status-chip.available{
  background: #EBFFF8;
  color: #25B8C2;
}
.status-chip.conflict{
  background: #F7EECE;
  color: #E3B946;
}
</style>


